import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Sparkles, ArrowRight } from "lucide-react";
import { startTour } from "@/lib/appTour";

export default function AuthPage() {
  const { login, signup } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // 'login' | 'signup'
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
      } else {
        if (!form.name.trim()) throw new Error("Please enter your name");
        await signup(form.name, form.email, form.password);
      }
      toast.success(mode === "login" ? "Welcome back!" : "Account created! 🎉");
      navigate("/subjects");
      // Auto-launch the product tour for brand-new signups.
      if (mode === "signup") {
        setTimeout(() => startTour({ navigate }), 800);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Something went wrong";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:flex flex-col justify-between p-12 text-white overflow-hidden">
        <img
          src="https://images.pexels.com/photos/29888423/pexels-photo-29888423.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=940"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--brimi-blue))]/85 via-[hsl(var(--brimi-blue))]/70 to-[hsl(var(--brimi-coral))]/80" />
        <div className="relative">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <span className="font-black text-2xl">Brimi AI</span>
          </Link>
        </div>
        <div className="relative">
          <h2 className="text-4xl xl:text-5xl font-black leading-[1.1] mb-4">
            Learn smarter,
            <br />
            ace your ICSE.
          </h2>
          <p className="text-white/85 text-lg max-w-md leading-relaxed">
            Your personal AI tutor that knows the syllabus — chapter by chapter.
            Save your chats, track your progress, and never get stuck again.
          </p>
        </div>
        <div className="relative text-xs text-white/70 uppercase tracking-widest">
          For Class IX · X · XI · XII students
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <Link
            to="/"
            className="lg:hidden inline-flex items-center gap-2 mb-8"
          >
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[hsl(var(--brimi-blue))] to-[hsl(var(--brimi-coral))] flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="font-black text-xl">Brimi AI</span>
          </Link>

          <h1 className="text-4xl sm:text-5xl font-black mb-2">
            {mode === "login" ? "Welcome back" : "Create account"}
          </h1>
          <p className="text-muted-foreground mb-8">
            {mode === "login"
              ? "Sign in to continue your learning journey."
              : "Join Brimi AI and start mastering your syllabus."}
          </p>

          <form onSubmit={submit} className="space-y-4">
            {mode === "signup" && (
              <div>
                <label className="text-sm font-semibold mb-1.5 block">
                  Full name
                </label>
                <input
                  data-testid="auth-name-input"
                  type="text"
                  value={form.name}
                  onChange={update("name")}
                  required
                  className="w-full rounded-2xl border border-border bg-card px-4 py-3 text-base outline-none focus:border-[hsl(var(--brimi-blue))] focus:ring-2 focus:ring-[hsl(var(--brimi-blue))]/20 transition"
                  placeholder="Ananya Sharma"
                />
              </div>
            )}
            <div>
              <label className="text-sm font-semibold mb-1.5 block">
                Email
              </label>
              <input
                data-testid="auth-email-input"
                type="email"
                value={form.email}
                onChange={update("email")}
                required
                className="w-full rounded-2xl border border-border bg-card px-4 py-3 text-base outline-none focus:border-[hsl(var(--brimi-blue))] focus:ring-2 focus:ring-[hsl(var(--brimi-blue))]/20 transition"
                placeholder="you@school.edu"
              />
            </div>
            <div>
              <label className="text-sm font-semibold mb-1.5 block">
                Password
              </label>
              <input
                data-testid="auth-password-input"
                type="password"
                value={form.password}
                onChange={update("password")}
                required
                minLength={6}
                className="w-full rounded-2xl border border-border bg-card px-4 py-3 text-base outline-none focus:border-[hsl(var(--brimi-blue))] focus:ring-2 focus:ring-[hsl(var(--brimi-blue))]/20 transition"
                placeholder="At least 6 characters"
              />
            </div>

            <button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={submitting}
              className="w-full pill-btn bg-[hsl(var(--brimi-blue))] hover:bg-[hsl(var(--brimi-blue))]/90 text-white py-4 text-base shadow-lg shadow-[hsl(var(--brimi-blue))]/25 disabled:opacity-50"
            >
              {submitting
                ? "Please wait…"
                : mode === "login"
                  ? "Sign in"
                  : "Create account"}
              {!submitting && <ArrowRight className="w-5 h-5" />}
            </button>

            {/* Divider */}
            <div className="relative py-2">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-background px-3 text-xs text-muted-foreground font-semibold">
                  OR
                </span>
              </div>
            </div>

            {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
            <button
              type="button"
              data-testid="auth-google-btn"
              onClick={() => {
                const redirectUrl = window.location.origin + "/auth/callback";
                window.location.href =
                  "https://auth.emergentagent.com/?redirect=" +
                  encodeURIComponent(redirectUrl);
              }}
              className="w-full pill-btn bg-white hover:bg-foreground/5 text-foreground border-2 border-border py-4 text-base inline-flex items-center justify-center gap-3"
            >
              <svg viewBox="0 0 48 48" className="w-5 h-5">
                <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
                <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
                <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.4-4.5 2.2-7.2 2.2-5.2 0-9.6-3.3-11.3-8l-6.5 5c3.3 6.5 10 11.2 17.8 11.2z"/>
                <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.2-4.1 5.6l6.2 5.2C41 34.5 44 29.6 44 24c0-1.3-.1-2.3-.4-3.5z"/>
              </svg>
              Continue with Google
            </button>

            {mode === "signup" && (
              <p
                data-testid="auth-legal-consent"
                className="text-xs text-muted-foreground text-center leading-relaxed"
              >
                By creating an account, you agree to our{" "}
                <Link to="/terms" className="font-semibold text-foreground hover:underline">Terms of Service</Link>{" "}
                and{" "}
                <Link to="/privacy" className="font-semibold text-foreground hover:underline">Privacy Policy</Link>.
              </p>
            )}
          </form>

          <div className="mt-6 text-center text-sm text-muted-foreground">
            {mode === "login" ? "New to Brimi?" : "Already have an account?"}{" "}
            <button
              data-testid="auth-toggle-mode"
              onClick={() => setMode(mode === "login" ? "signup" : "login")}
              className="font-bold text-[hsl(var(--brimi-blue))] hover:underline"
            >
              {mode === "login" ? "Create one" : "Sign in"}
            </button>
          </div>

          <div
            data-testid="auth-legal-links"
            className="mt-6 pt-5 border-t border-border/60 flex flex-wrap justify-center gap-x-4 gap-y-2 text-xs text-muted-foreground"
          >
            <Link to="/about" className="hover:text-foreground">About</Link>
            <Link to="/contact" className="hover:text-foreground">Contact</Link>
            <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
            <Link to="/terms" className="hover:text-foreground">Terms</Link>
            <Link to="/refund" className="hover:text-foreground">Refund</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
